import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap, Eye, Network, Power, Phone, Lock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../utils/api';

const NewServiceRequest = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    service_type: 'electrical',
    description: '',
    priority: 'medium',
    address: '',
    preferred_date: '',
    preferred_time: ''
  });

  const services = [
    { value: 'electrical', label: 'Electrical', icon: Zap },
    { value: 'cctv', label: 'CCTV', icon: Eye },
    { value: 'networking', label: 'Networking', icon: Network },
    { value: 'generator', label: 'Generator', icon: Power },
    { value: 'intercom', label: 'Intercom', icon: Phone },
    { value: 'access_control', label: 'Access Control', icon: Lock }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await api('/api/service-requests', {
        method: 'POST',
        body: JSON.stringify(formData)
      });
      toast.success(`Service request ${result.ticket_number} created!`);
      navigate('/customer/requests');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="new-request-form" className="max-w-2xl mx-auto space-y-6">
      <h1 className="font-heading text-3xl font-bold text-white">New Service Request</h1>

      <form onSubmit={handleSubmit} className="card p-6 space-y-6">
        <div>
          <label className="block text-sm font-medium text-white mb-2">Service Type</label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {services.map((service) => (
              <button
                key={service.value}
                type="button"
                onClick={() => setFormData({ ...formData, service_type: service.value })}
                className={`p-4 border-2 text-left transition-colors duration-200 ${
                  formData.service_type === service.value
                    ? 'border-secondary bg-secondary/10'
                    : 'border-gray-800 hover:border-slate-300'
                }`}
                data-testid={`service-option-${service.value}`}
              >
                <service.icon className="w-6 h-6 text-white mb-2" />
                <span className="font-medium text-sm">{service.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-white mb-2">Description</label>
          <textarea
            className="input min-h-[120px]"
            placeholder="Describe the issue or service needed..."
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            required
            data-testid="description-input"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-white mb-2">Priority</label>
          <select
            className="input"
            value={formData.priority}
            onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
            data-testid="priority-select"
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-white mb-2">Service Address</label>
          <input
            type="text"
            className="input"
            placeholder="Enter the service location address"
            value={formData.address}
            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            required
            data-testid="address-input"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-white mb-2">Preferred Date</label>
            <input
              type="date"
              className="input"
              value={formData.preferred_date}
              onChange={(e) => setFormData({ ...formData, preferred_date: e.target.value })}
              data-testid="date-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-white mb-2">Preferred Time</label>
            <select
              className="input"
              value={formData.preferred_time}
              onChange={(e) => setFormData({ ...formData, preferred_time: e.target.value })}
              data-testid="time-select"
            >
              <option value="">Any time</option>
              <option value="morning">Morning (8AM-12PM)</option>
              <option value="afternoon">Afternoon (12PM-5PM)</option>
              <option value="evening">Evening (5PM-8PM)</option>
            </select>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="btn-primary w-full flex items-center justify-center gap-2"
          data-testid="submit-request-btn"
        >
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          Submit Request
        </button>
      </form>
    </div>
  );
};

export default NewServiceRequest;
